import { useState, useRef, useCallback } from 'react';
import HeroCard from '../HeroCard';
import { TierTooltip } from './TierTooltip';
import type { TierHero } from '../types';
import './TierRow.css';

interface TierRowProps {
  tier: 'S' | 'A' | 'B' | 'C' | 'D';
  heroes: TierHero[];
  beta: number;
}

const TIER_COLORS = {
  S: { bg: 'var(--tier-s-bg)', border: 'var(--tier-s-border)', text: 'var(--tier-s-text)' },
  A: { bg: 'var(--tier-a-bg)', border: 'var(--tier-a-border)', text: 'var(--tier-a-text)' },
  B: { bg: 'var(--tier-b-bg)', border: 'var(--tier-b-border)', text: 'var(--tier-b-text)' },
  C: { bg: 'var(--tier-c-bg)', border: 'var(--tier-c-border)', text: 'var(--tier-c-text)' },
  D: { bg: 'var(--tier-d-bg)', border: 'var(--tier-d-border)', text: 'var(--tier-d-text)' }
};

export function TierRow({ tier, heroes, beta }: TierRowProps) {
  const colors = TIER_COLORS[tier];
  const [activeHero, setActiveHero] = useState<TierHero | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const calculateTooltipPosition = (rect: DOMRect) => {
    const isMobile = window.innerWidth <= 768;
    const tooltipWidth = isMobile ? 160 : 200;
    const tooltipHeight = isMobile ? 140 : 180;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // モバイル: カードの直下に表示（より直感的）
    // デスクトップ: カードの上に表示（下に行が重なるため）
    let top: number;
    if (isMobile) {
      // モバイルではカードの下に表示
      top = rect.bottom + 8;
      // 画面下に収まらない場合は上に表示
      if (top + tooltipHeight > viewportHeight - 10) {
        top = rect.top - tooltipHeight - 8;
      }
    } else {
      // デスクトップではカードの上に表示
      top = rect.top - tooltipHeight - 10;
      if (top < 10) {
        top = rect.bottom + 10;
      }
    }
    
    // 水平方向の位置計算（常にカードの中央に合わせる）
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    
    // 画面端での調整
    if (left < 8) {
      left = 8;
    } else if (left + tooltipWidth > viewportWidth - 8) {
      left = viewportWidth - tooltipWidth - 8;
    }
    
    return { top, left, isMobile };
  };

  const handleMouseEnter = useCallback((hero: TierHero, rect: DOMRect) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    const position = calculateTooltipPosition(rect);
    setTooltipPosition(position);
    setActiveHero(hero);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setActiveHero(null);
    }, 150);
  }, []);

  const handleTooltipMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleTooltipMouseLeave = useCallback(() => {
    setActiveHero(null);
  }, []);

  if (heroes.length === 0) {
    return (
      <div className="tier-row empty">
        <div 
          className="tier-label"
          style={{ 
            backgroundColor: colors.bg, 
            borderColor: colors.border,
            color: colors.text 
          }}
        >
          {tier}
        </div>
        <div className="tier-heroes empty-message">
          No heroes in this tier
        </div>
      </div>
    );
  }

  return (
    <div className="tier-row">
      <div 
        className="tier-label"
        style={{ 
          backgroundColor: colors.bg, 
          borderColor: colors.border,
          color: colors.text 
        }}
      >
        {tier}
      </div>
      <div className="tier-heroes">
        {heroes.map((tierHero) => (
          <div 
            key={tierHero.hero.hero_id}
            onMouseEnter={(e) => handleMouseEnter(tierHero, e.currentTarget.getBoundingClientRect())}
            onMouseLeave={handleMouseLeave}
          >
            <HeroCard
              hero={tierHero.hero}
              onMouseEnter={() => {}}
              onMouseLeave={() => {}}
            />
          </div>
        ))}
      </div>
      
      {activeHero && (
        <TierTooltip
          hero={activeHero}
          beta={beta}
          position={tooltipPosition}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        />
      )}
    </div>
  );
}
